import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './HourlyRates.css';

function HourlyRates({ tenantId }) {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rate: 0,
    description: '',
    isDefault: false,
    roleId: ''
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadRates();
      loadRoles();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setRates(data);
    } catch (error) {
      console.error('Error loading rates:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await api.get('/roles', tenantId);
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRate) {
        await api.put(`/hourly-rates/${editingRate.id}`, formData, tenantId);
      } else {
        await api.post('/hourly-rates', formData, tenantId);
      }
      setShowForm(false);
      setEditingRate(null);
      setFormData({
        name: '',
        rate: 0,
        description: '',
        isDefault: false,
        roleId: ''
      });
      loadRates();
    } catch (error) {
      console.error('Error saving rate:', error);
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData(rate);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Stundensatz wirklich löschen?')) {
      try {
        await api.delete(`/hourly-rates/${id}`, tenantId);
        loadRates();
      } catch (error) {
        console.error('Error deleting rate:', error);
      }
    }
  };

  return (
    <div className="hourly-rates">
      <div className="page-header">
        <h1>Stundensätze</h1>
        <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
          Neuer Stundensatz
        </button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingRate ? 'Stundensatz bearbeiten' : 'Neuer Stundensatz'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Rolle *</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => {
                    const selectedRole = roles.find(r => r.id === e.target.value);
                    setFormData({ 
                      ...formData, 
                      roleId: e.target.value,
                      name: selectedRole ? selectedRole.name : formData.name
                    });
                  }}
                  required
                  disabled={!!editingRate}
                >
                  <option value="">Bitte wählen</option>
                  {roles.filter(role => {
                    // Beim Bearbeiten: Nur die aktuelle Rolle anzeigen
                    if (editingRate && editingRate.roleId) {
                      return role.id === editingRate.roleId;
                    }
                    // Beim Erstellen: Nur Rollen ohne Stundensatz anzeigen
                    return !rates.find(r => r.roleId === role.id);
                  }).map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {editingRate && (
                  <p className="form-hint">Rolle kann nach Erstellung nicht mehr geändert werden</p>
                )}
              </div>
              <div className="form-group">
                <label>Stundensatz ({currency}/h)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                    setFormData({ ...formData, rate: val });
                  }}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  Standard-Stundensatz
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingRate ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingRate(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={rates}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'roleId',
            header: 'Rolle',
            sortType: 'string',
            accessor: (row) => {
              if (row.roleId) {
                const role = roles.find(r => r.id === row.roleId);
                return role ? role.name : (row.name || '');
              }
              return row.name || '';
            },
            render: (row) => {
              const roleName = row.roleId 
                ? (roles.find(r => r.id === row.roleId)?.name || row.name || '')
                : (row.name || '');
              return (
                <button 
                  className="link-button" 
                  onClick={() => navigate(`/hourly-rates/${row.id}`)}
                >
                  {roleName}
                </button>
              );
            }
          },
          {
            key: 'rate',
            header: 'Stundensatz',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.rate || 0,
            render: (row) => `${row.rate.toFixed(2)} ${currency}/h`
          },
          {
            key: 'description',
            header: 'Beschreibung',
            sortType: 'string',
            accessor: (row) => row.description || ''
          },
          {
            key: 'isDefault',
            header: 'Standard',
            sortType: 'string',
            accessor: (row) => row.isDefault ? 'Ja' : 'Nein'
          }
        ]}
        defaultSort={{ key: 'roleId', direction: 'asc' }}
        emptyMessage="Keine Stundensätze vorhanden"
      />
    </div>
  );
}

export default HourlyRates;

